import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { useTitle } from "../../../hooks/useTitle";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Button from "../../../components/BaseUI/Button";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import Input from "../../../components/BaseUI/Input";
import Select from "../../../components/BaseUI/Select";
import SidebarContainer from "../../../components/CustomUI/SidebarContainer";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import Label from "../../../components/BaseUI/Label";
import Lozenge from "../../../components/BaseUI/Lozenge";
import { FiLayers, FiPlus, FiEdit2, FiTrash2 } from "react-icons/fi";
import { withAuth } from "../../../components/HOC/withAuth";
import Checkbox from "../../../components/BaseUI/Checkbox";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const emptyBankAccount = (overrides = {}) => ({
  label: "",
  bankRecipientName: "",
  bankBranchName: "",
  bankIban: "",
  isDefault: false,
  ...overrides,
});

const STATUS_OPTIONS = [
  { label: "Aktif", value: "active" },
  { label: "Pasif", value: "inactive" },
];

const SystemCompanies = () => {
  useTitle("Rovion MES - Sistem Firmaları");
  const { getToken, getUser, loading: authLoading } = useAuth();
  const currentUser = getUser();
  const isSystemAdmin = currentUser?.role?.toLowerCase() === "admin";

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    companyId: null,
  });

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    email: "",
    phone: "",
    address: "",
    taxNumber: "",
    taxOffice: "",
    prefix: "",
    dbName: "",
    status: "active",
    bankAccounts: [],
  });

  const headers = [
    {
      name: "Firma Adı",
      dataType: "text",
      id: 0,
      customSorter: (a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || "")),
    },
    {
      name: "Firma Kodu",
      dataType: "text",
      id: 1,
      customSorter: (a, b) =>
        String(a?.code || "").localeCompare(String(b?.code || "")),
    },
    {
      name: "Önek",
      dataType: "text",
      id: 7,
      customSorter: (a, b) =>
        String(a?.prefix || "").localeCompare(String(b?.prefix || "")),
    },
    {
      name: "E-posta",
      dataType: "text",
      id: 2,
      customSorter: (a, b) =>
        String(a?.email || "").localeCompare(String(b?.email || "")),
    },
    {
      name: "Telefon",
      dataType: "text",
      id: 3,
      customSorter: (a, b) =>
        String(a?.phone || "").localeCompare(String(b?.phone || "")),
    },
    {
      name: "Vergi No",
      dataType: "text",
      id: 4,
      customSorter: (a, b) =>
        String(a?.taxNumber || "").localeCompare(String(b?.taxNumber || "")),
    },
    {
      name: "Durum",
      dataType: "text",
      id: 5,
      customSorter: (a, b) =>
        String(a?.status || "").localeCompare(String(b?.status || "")),
    },
    { name: "İşlemler", id: 6, sortable: false },
  ];

  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("system-companies-list", headers);

  const addToast = (message, type = "info") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== id)),
      4000,
    );
  };

  useEffect(() => {
    if (isSystemAdmin) {
      fetchCompanies();
    }
  }, [isSystemAdmin]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/system-companies`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanies(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Firmalar yüklenemedi:", error);
      addToast("Sistem firmaları yüklenemedi", "danger");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingCompany(null);
    setFormData({
      name: "",
      code: "",
      email: "",
      phone: "",
      address: "",
      taxNumber: "",
      taxOffice: "",
      prefix: "",
      dbName: "",
      status: "active",
      bankAccounts: [],
    });
  };

  const handleCreate = () => {
    resetForm();
    setFormData((prev) => ({
      ...prev,
      bankAccounts: [emptyBankAccount({ isDefault: true, label: "Ana Hesap" })],
    }));
    setSidebarOpened(true);
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    const accounts = Array.isArray(company.bankAccounts)
      ? company.bankAccounts.map((a) => ({
          _id: a._id,
          label: a.label || "",
          bankRecipientName: a.bankRecipientName || "",
          bankBranchName: a.bankBranchName || "",
          bankIban: a.bankIban || "",
          isDefault: !!a.isDefault,
        }))
      : [];
    setFormData({
      name: company.name || "",
      code: company.code || "",
      email: company.email || "",
      phone: company.phone || "",
      address: company.address || "",
      taxNumber: company.taxNumber || "",
      taxOffice: company.taxOffice || "",
      prefix: company.prefix || "",
      dbName: company.dbName || "",
      status: company.status || "active",
      bankAccounts: accounts,
    });
    setSidebarOpened(true);
  };

  const handleClose = () => {
    setSidebarOpened(false);
    resetForm();
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      addToast("Firma Adı zorunludur", "warning");
      return;
    }
    if (!formData.code.trim()) {
      addToast("Firma Kodu zorunludur", "warning");
      return;
    }
    if (!formData.email.trim()) {
      addToast("E-posta zorunludur", "warning");
      return;
    }
    if (!formData.phone.trim()) {
      addToast("Telefon zorunludur", "warning");
      return;
    }
    if (!formData.address.trim()) {
      addToast("Adres zorunludur", "warning");
      return;
    }

    try {
      setLoading(true);
      const token = getToken();

      const payload = {
        name: formData.name.trim(),
        code: formData.code.trim().toUpperCase(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        address: formData.address.trim(),
        taxNumber: formData.taxNumber.trim() || undefined,
        taxOffice: formData.taxOffice.trim() || undefined,
        prefix: formData.prefix.trim().toUpperCase() || undefined,
        dbName: formData.dbName.trim() || undefined,
        status: formData.status,
        bankAccounts: (formData.bankAccounts || []).map((a, index) => ({
          ...(a._id ? { _id: a._id } : {}),
          label: (a.label || `Hesap ${index + 1}`).trim(),
          bankRecipientName: (a.bankRecipientName || "").trim(),
          bankBranchName: (a.bankBranchName || "").trim(),
          bankIban: (a.bankIban || "").trim(),
          isDefault: !!a.isDefault,
        })),
      };

      let response;
      if (editingCompany) {
        response = await axios.put(
          `${API_URL}/api/system-companies/${editingCompany._id}`,
          payload,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
      } else {
        response = await axios.post(
          `${API_URL}/api/system-companies`,
          payload,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
      }

      if (response.data.success) {
        addToast(
          response.data.message || "Firma başarıyla kaydedildi",
          "success",
        );
        setSidebarOpened(false);
        resetForm();
        fetchCompanies();
      } else {
        addToast(response.data.message || "Firma kaydedilemedi", "danger");
      }
    } catch (error) {
      console.error("Firma kaydetme hatası:", error);
      addToast(
        error.response?.data?.message || "Firma kaydedilemedi",
        "danger",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmModal.companyId) return;
    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.delete(
        `${API_URL}/api/system-companies/${confirmModal.companyId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success) {
        addToast(response.data.message || "Firma başarıyla silindi", "success");
        setConfirmModal({ isOpen: false, companyId: null });
        fetchCompanies();
      } else {
        addToast(response.data.message || "Silme işlemi başarısız", "danger");
      }
    } catch (error) {
      addToast(error.response?.data?.message || "Firma silinemedi", "danger");
      setConfirmModal({ isOpen: false, companyId: null });
    } finally {
      setLoading(false);
    }
  };

  const getStatusAppearance = (status) => {
    return status === "active" ? "success-subtle" : "danger-subtle";
  };

  const getStatusLabel = (status) => {
    const opt = STATUS_OPTIONS.find((o) => o.value === status);
    return opt ? opt.label : status || "-";
  };

  const selectedStatusOption = formData.status ? [formData.status] : [];

  if (authLoading) {
    return (
      <AppPage title="Yükleniyor..." loading={true}>
        <CardContainer>
          <div className="text-center p-5">
            <FiLayers
              className="spin text-primary"
              size={48}
              style={{ width: "48px", height: "48px" }}
            />
            <p className="mt-2">Yetkilendirme kontrol ediliyor...</p>
          </div>
        </CardContainer>
      </AppPage>
    );
  }

  if (!isSystemAdmin) {
    return (
      <AppPage title="Erişim Engellendi">
        <CardContainer>
          <div className="text-center p-5">
            <FiLayers size={64} className="text-danger mb-3" />
            <h3>Bu sayfaya erişim yetkiniz yok</h3>
            <p className="text-muted">
              Bu sayfa yalnızca sistem yöneticileri içindir.
            </p>
          </div>
        </CardContainer>
      </AppPage>
    );
  }

  return (
    <AppPage
      title="Sistem Firmaları"
      subtitle="Sistemde tanımlı global tenant firmalarını yönetin"
      icon={<FiLayers />}
      actions={[
        <Button
          appearance="primary-animated"
          onClick={handleCreate}
          icon={<FiPlus />}
          key="add-btn"
        >
          Yeni Firma Ekle
        </Button>,
      ]}
    >
      <CardContainer>
        {loading && companies.length === 0 ? (
          <div className="loading-container text-center p-5">
            <FiLayers className="spin text-primary" size={48} />
            <p className="mt-2">Sistem firmaları yükleniyor...</p>
          </div>
        ) : (
          <>
            <Table
              columns={headers}
              visibleColumns={visibleColumns}
              tableId="system-companies-list"
              checkboxSelection
              loading={loading}
              viewOptions={
                <ListViewOptionsPanel
                  columns={headers}
                  visibleColumns={visibleColumns}
                  onToggleColumn={toggleColumnVisibility}
                  onResetColumns={resetToDefault}
                  showApply={false}
                  showReset={false}
                />
              }
            >
              {companies.map((company) => (
                <Tr key={company._id} id={company._id} data={company}>
                  {visibleColumns.includes(0) && (
                    <Td>
                      <strong>{company.name}</strong>
                    </Td>
                  )}
                  {visibleColumns.includes(1) && (
                    <Td>
                      <code>{company.code}</code>
                    </Td>
                  )}
                  {visibleColumns.includes(7) && (
                    <Td>
                      {company.prefix ? <code>{company.prefix}</code> : "—"}
                    </Td>
                  )}
                  {visibleColumns.includes(2) && (
                    <Td>{company.email || "-"}</Td>
                  )}
                  {visibleColumns.includes(3) && (
                    <Td>{company.phone || "-"}</Td>
                  )}
                  {visibleColumns.includes(4) && (
                    <Td>
                      {company.taxNumber || "-"}{" "}
                      {company.taxOffice ? `(${company.taxOffice})` : ""}
                    </Td>
                  )}
                  {visibleColumns.includes(5) && (
                    <Td>
                      <Lozenge appearance={getStatusAppearance(company.status)}>
                        {getStatusLabel(company.status)}
                      </Lozenge>
                    </Td>
                  )}
                  {visibleColumns.includes(6) && (
                    <Td>
                      <div className="d-flex align-items-center gap-1">
                        <IconButton
                          icon={<FiEdit2 style={{ fontSize: "18px" }} />}
                          appearance="subtle"
                          shape="circle"
                          onClick={() => handleEdit(company)}
                        />
                        <IconButton
                          icon={<FiTrash2 style={{ fontSize: "18px" }} />}
                          appearance="subtle"
                          shape="circle"
                          onClick={() =>
                            setConfirmModal({
                              isOpen: true,
                              companyId: company._id,
                            })
                          }
                        />
                      </div>
                    </Td>
                  )}
                </Tr>
              ))}
            </Table>

            {companies.length === 0 && (
              <div className="empty-state text-center p-5">
                <FiLayers size={64} className="text-muted mb-3" />
                <p>Kayıtlı sistem firması bulunmamaktadır.</p>
              </div>
            )}
          </>
        )}
      </CardContainer>

      <SidebarContainer
        show={sidebarOpened}
        title={editingCompany ? "Firmayı Düzenle" : "Yeni Sistem Firması Ekle"}
        onClose={handleClose}
      >
        <div className="d-flex flex-column" style={{ height: "100%" }}>
          <div className="flex-grow-1 overflow-auto pe-1">
            <div className="mt-2">
              <Label className="mb-0">Firma Adı *</Label>
              <Input
                type="text"
                placeholder="Firma Adı"
                value={formData.name}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, name: value }))
                }
              />
            </div>

            <div className="mt-3">
              <Label className="mb-0">Firma Kodu *</Label>
              <Input
                type="text"
                placeholder="Örn: ABC"
                value={formData.code}
                disabled={!!editingCompany}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, code: value }))
                }
              />
              {editingCompany && (
                <small className="text-muted d-block mt-1">
                  Kayıtlı firma kodu değiştirilemez.
                </small>
              )}
            </div>

            <div className="mt-3">
              <Label className="mb-0">Firma Öneki</Label>
              <Input
                type="text"
                placeholder="Örn: NEF"
                value={formData.prefix}
                onChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    prefix: String(value || "").toUpperCase(),
                  }))
                }
              />
            </div>

            <div className="mt-3">
              <Label className="mb-0">E-posta *</Label>
              <Input
                type="email"
                placeholder="info@firma.com"
                value={formData.email}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, email: value }))
                }
              />
            </div>

            <div className="mt-3">
              <Label className="mb-0">Telefon *</Label>
              <Input
                type="text"
                placeholder="Telefon"
                value={formData.phone}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, phone: value }))
                }
              />
            </div>

            <div className="mt-3">
              <Label className="mb-0">Vergi Numarası</Label>
              <Input
                type="text"
                placeholder="Vergi Numarası"
                value={formData.taxNumber}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, taxNumber: value }))
                }
              />
            </div>

            <div className="mt-3">
              <Label className="mb-0">Vergi Dairesi</Label>
              <Input
                type="text"
                placeholder="Vergi Dairesi"
                value={formData.taxOffice}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, taxOffice: value }))
                }
              />
            </div>

            <div className="mt-3">
              <Label className="mb-0">Adres *</Label>
              <textarea
                className="form-control"
                placeholder="Adres"
                value={formData.address}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, address: e.target.value }))
                }
                rows={3}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontFamily: "inherit",
                  fontSize: "14px",
                  resize: "vertical",
                }}
              />
            </div>

            <div className="mt-3">
              <Select
                label="Durum *"
                options={STATUS_OPTIONS}
                selected={selectedStatusOption}
                onSelected={(values) =>
                  setFormData((prev) => ({
                    ...prev,
                    status: values?.[0]?.value || "active",
                  }))
                }
                unique
                placeholder="Durum seçin"
              />
            </div>

            <div className="mt-4 pt-3 border-top">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <Label className="mb-0">Banka Hesapları</Label>
                <Button
                  appearance="primary-subtle"
                  icon={<FiPlus />}
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      bankAccounts: [
                        ...(prev.bankAccounts || []),
                        emptyBankAccount({
                          isDefault: (prev.bankAccounts || []).length === 0,
                          label: `Hesap ${(prev.bankAccounts || []).length + 1}`,
                        }),
                      ],
                    }))
                  }
                >
                  Hesap Ekle
                </Button>
              </div>
              <p className="text-muted small mb-3">
                Teklif PDF&apos;inde kullanılacak banka hesaplarını buradan
                tanımlayın. Sipariş formunda bunlardan biri seçilir.
              </p>

              {(formData.bankAccounts || []).length === 0 ? (
                <div className="text-muted small p-3 border rounded bg-light">
                  Henüz banka hesabı eklenmedi.
                </div>
              ) : (
                (formData.bankAccounts || []).map((account, index) => (
                  <div
                    key={account._id || index}
                    className="p-3 mb-3 border rounded bg-white"
                  >
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <strong className="small">Hesap {index + 1}</strong>
                      <IconButton
                        icon={<FiTrash2 />}
                        label="Sil"
                        appearance="subtle"
                        shape="circle"
                        onClick={() =>
                          setFormData((prev) => {
                            const next = (prev.bankAccounts || []).filter(
                              (_, i) => i !== index,
                            );
                            if (
                              next.length > 0 &&
                              !next.some((a) => a.isDefault)
                            ) {
                              next[0] = { ...next[0], isDefault: true };
                            }
                            return { ...prev, bankAccounts: next };
                          })
                        }
                      />
                    </div>
                    <Input
                      type="text"
                      label="Hesap Etiketi"
                      value={account.label}
                      onChange={(value) =>
                        setFormData((prev) => {
                          const next = [...(prev.bankAccounts || [])];
                          next[index] = { ...next[index], label: value };
                          return { ...prev, bankAccounts: next };
                        })
                      }
                      placeholder="Hesap etiketi"
                    />
                    <Input
                      type="text"
                      label="Alıcı Adı"
                      value={account.bankRecipientName}
                      onChange={(value) =>
                        setFormData((prev) => {
                          const next = [...(prev.bankAccounts || [])];
                          next[index] = {
                            ...next[index],
                            bankRecipientName: value,
                          };
                          return { ...prev, bankAccounts: next };
                        })
                      }
                      placeholder="Alıcı adı"
                    />
                    <Input
                      type="text"
                      label="Banka / Şube"
                      value={account.bankBranchName}
                      onChange={(value) =>
                        setFormData((prev) => {
                          const next = [...(prev.bankAccounts || [])];
                          next[index] = {
                            ...next[index],
                            bankBranchName: value,
                          };
                          return { ...prev, bankAccounts: next };
                        })
                      }
                      placeholder="Banka / şube"
                    />
                    <Input
                      type="text"
                      label="IBAN"
                      value={account.bankIban}
                      onChange={(value) =>
                        setFormData((prev) => {
                          const next = [...(prev.bankAccounts || [])];
                          next[index] = { ...next[index], bankIban: value };
                          return { ...prev, bankAccounts: next };
                        })
                      }
                      placeholder="IBAN"
                    />
                    {/* <div className="mt-2">
                      <Checkbox
                        value={`default-bank-${index}`}
                        label="Varsayılan hesap"
                        isChecked={!!account.isDefault}
                        onChange={({ checked }) => {
                          if (!checked) return;
                          setFormData((prev) => ({
                            ...prev,
                            bankAccounts: (prev.bankAccounts || []).map(
                              (a, i) => ({
                                ...a,
                                isDefault: i === index,
                              }),
                            ),
                          }));
                        }}
                      />
                    </div> */}
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="mt-5 d-flex gap-2 justify-content-end">
            <Button appearance="danger-subtle" onClick={handleClose}>
              İptal
            </Button>
            <Button
              appearance="primary-animated"
              onClick={handleSubmit}
              loading={loading}
            >
              {editingCompany ? "Güncelle" : "Kaydet"}
            </Button>
          </div>
        </div>
      </SidebarContainer>

      <ToastContainer>
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            title={toast.message}
            appearance={toast.type}
            onClose={() =>
              setToasts((prev) => prev.filter((t) => t.id !== toast.id))
            }
          />
        ))}
      </ToastContainer>

      <Modal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, companyId: null })}
        width="small"
      >
        <ModalHeader>Sistem Firmasını Sil</ModalHeader>
        <ModalBody>
          <p>
            Bu firmayı silmek istediğinizden emin misiniz? Bu işlem Firmain
            global profilini kaldıracaktır. Bu işlem geri alınamaz.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            appearance="subtle"
            onClick={() => setConfirmModal({ isOpen: false, companyId: null })}
          >
            İptal
          </Button>
          <Button appearance="danger" onClick={handleDelete} loading={loading}>
            Firmayı Sil
          </Button>
        </ModalFooter>
      </Modal>
    </AppPage>
  );
};

export default withAuth(SystemCompanies, ["admin"]);
